import {
  createNestablePublicClientApplication,
  IPublicClientApplication,
  AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { app as teamsApp } from '@microsoft/teams-js';
import { msalConfig, graphScopes } from './msalConfig';
import { getDataverseScopes } from '../services/environmentService';

let pcaInstance: IPublicClientApplication | null = null;

export async function initializeAuth(): Promise<IPublicClientApplication> {
  await teamsApp.initialize(); // TeamsJS MUST initialize first
  try {
    pcaInstance = await createNestablePublicClientApplication(msalConfig);
  } catch {
    // Fallback for legacy Teams clients that do not support NAA
    const pca = new PublicClientApplication(msalConfig);
    await pca.initialize();
    pcaInstance = pca;
  }
  return pcaInstance;
}

export async function getAccount(
  pca: IPublicClientApplication
): Promise<AccountInfo | null> {
  let account = pca.getActiveAccount();
  if (account) return account;
  const context = await teamsApp.getContext();
  const accounts = pca.getAllAccounts();
  account = accounts.find(a => a.homeAccountId.startsWith(context.user?.id ?? '')) ?? null;
  if (account) pca.setActiveAccount(account);
  return account;
}

export async function getToken(
  pca: IPublicClientApplication,
  scopes: string[]
): Promise<string> {
  const account = await getAccount(pca);
  try {
    const response = await pca.acquireTokenSilent({ scopes, account: account ?? undefined });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const response = await pca.acquireTokenPopup({ scopes, account: account ?? undefined });
      return response.accessToken;
    }
    throw error;
  }
}

export const getGraphToken = (pca: IPublicClientApplication) =>
  getToken(pca, graphScopes);

export const getDataverseToken = (pca: IPublicClientApplication) =>
  getToken(pca, getDataverseScopes());

export async function getDataverseHeaders(
  pca: IPublicClientApplication
): Promise<Record<string, string>> {
  const token = await getDataverseToken(pca);
  return {
    Authorization: `Bearer ${token}`,
    'OData-MaxVersion': '4.0',
    'OData-Version': '4.0',
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
}

export async function getGraphHeaders(
  pca: IPublicClientApplication
): Promise<Record<string, string>> {
  const token = await getGraphToken(pca);
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export async function checkLicense(_pca: IPublicClientApplication): Promise<boolean> {
  // License lookup calls GET /api/license/status?key=<licenseKey> on the Dagrun platform.
  // The platform endpoint is un-authenticated and returns the license record when the key
  // is provided as a URL parameter. The licenseKey is baked in at build time via env var.
  const licenseKey = process.env.REACT_APP_LICENSE_KEY;
  const platformUrl = process.env.REACT_APP_PLATFORM_URL;
  if (!licenseKey || !platformUrl) return false;
  try {
    const response = await fetch(
      `${platformUrl}/api/license/status?key=${encodeURIComponent(licenseKey)}`,
    );
    if (!response.ok) return false;
    const result = await response.json();
    return result.status === 'active' && result.isExpired === false;
  } catch {
    return false;
  }
}
